import "./MobileHomePrototypePage.css";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Offcanvas } from "react-bootstrap";
import {
  FaBell,
  FaBriefcase,
  FaCalendarAlt,
  FaChevronRight,
  FaHome,
  FaMapMarkerAlt,
  FaTint,
  FaUser,
  FaUsers,
} from "react-icons/fa";
import { FaBoltLightning } from "react-icons/fa6";
import { IoCalendarOutline } from "react-icons/io5";
import Indice from "../../components/0. Header/Indice";
import PersonAvatar from "../../components/0. Utilidades/PersonAvatar";
import { getApiUrl } from "../../utils/apiUrl";

const PAULA_RUT = "10928610-9";
const LOWERCASE_TITLE_WORDS = new Set([
  "a",
  "al",
  "de",
  "del",
  "e",
  "el",
  "en",
  "la",
  "las",
  "los",
  "para",
  "por",
  "un",
  "una",
  "y",
]);

const normalizeTitle = (value) => {
  if (!value) return "";

  return String(value)
    .toLocaleLowerCase("es-CL")
    .split(/\s+/)
    .filter(Boolean)
    .map((word, index) => {
      if (index > 0 && LOWERCASE_TITLE_WORDS.has(word)) {
        return word;
      }

      return word.charAt(0).toLocaleUpperCase("es-CL") + word.slice(1);
    })
    .join(" ");
};

const parseDate = (value) => {
  if (!value) return null;

  const cleanValue = String(value).trim();

  if (/^\d{4}-\d{2}-\d{2}$/.test(cleanValue)) {
    const [year, month, day] = cleanValue.split("-").map(Number);
    return new Date(year, month - 1, day);
  }

  if (/^\d{2}-\d{2}-\d{4}$/.test(cleanValue)) {
    const [day, month, year] = cleanValue.split("-").map(Number);
    return new Date(year, month - 1, day);
  }

  const parsedDate = new Date(cleanValue);
  if (!Number.isNaN(parsedDate.getTime())) {
    return new Date(
      parsedDate.getFullYear(),
      parsedDate.getMonth(),
      parsedDate.getDate(),
    );
  }

  return null;
};

const parseItemStartDate = (item) => {
  const value = item.f_inicio_date || item.f_inicio;
  if (typeof value === "number") return null;
  return parseDate(value);
};

const parseItemEndDate = (item) => {
  const value = item.f_termino_date || item.f_termino;
  if (typeof value === "number") return null;
  return parseDate(value);
};

const getToday = () => {
  const today = new Date();
  return new Date(today.getFullYear(), today.getMonth(), today.getDate());
};

const formatDate = (date) => {
  if (!date) return "Sin fecha";
  return new Intl.DateTimeFormat("es-CL", {
    day: "numeric",
    month: "long",
  }).format(date);
};

const formatEventDate = (date) => {
  if (!date) return "Fecha por confirmar";

  const today = getToday();
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);

  if (date.getTime() === today.getTime()) return "Hoy";
  if (date.getTime() === tomorrow.getTime()) return "Mañana";

  return formatDate(date);
};

const normalizeUpcomingItem = (item, type) => {
  const startDate = parseItemStartDate(item);
  const endDate = parseItemEndDate(item) || startDate;

  return {
    ...item,
    type,
    startDate,
    endDate,
  };
};

const getUpcomingItems = (items = [], type) => {
  const today = getToday();

  return items
    .map((item) => normalizeUpcomingItem(item, type))
    .filter((item) => item.endDate && item.endDate >= today)
    .sort((a, b) => a.startDate - b.startDate);
};

const MobileHomePrototypePage = () => {
  const [showNav, setShowNav] = useState(false);
  const [chromeHidden, setChromeHidden] = useState(false);
  const [maestra, setMaestra] = useState(null);
  const [globalAcademias, setGlobalAcademias] = useState([]);
  const [isLoadingProfile, setIsLoadingProfile] = useState(true);
  const [profileError, setProfileError] = useState("");

  useEffect(() => {
    let lastScrollY = window.scrollY;

    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const isScrollingDown = currentScrollY > lastScrollY;

      setChromeHidden(isScrollingDown && currentScrollY > 72);
      lastScrollY = currentScrollY;
    };

    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    let isActive = true;

    setIsLoadingProfile(true);
    setProfileError("");

    fetch(getApiUrl(`api/perfiles/maestras/${PAULA_RUT}/`), {
      credentials: "include",
      signal: controller.signal,
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error("No se pudo cargar el perfil.");
        }
        return response.json();
      })
      .then((data) => {
        if (isActive) {
          setMaestra(data);
        }
      })
      .catch((error) => {
        if (isActive && error.name !== "AbortError") {
          setProfileError(error.message);
        }
      })
      .finally(() => {
        if (isActive) {
          setIsLoadingProfile(false);
        }
      });

    return () => {
      isActive = false;
      controller.abort();
    };
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    let isActive = true;

    Promise.all([
      fetch(getApiUrl("api/perfiles/cursos/"), {
        credentials: "include",
        signal: controller.signal,
      }),
      fetch(getApiUrl("api/perfiles/actividades/"), {
        credentials: "include",
        signal: controller.signal,
      }),
    ])
      .then((responses) => {
        responses.forEach((response) => {
          if (!response.ok) {
            throw new Error("No se pudieron cargar las próximas academias.");
          }
        });

        return Promise.all(responses.map((response) => response.json()));
      })
      .then(([cursos, actividades]) => {
        if (!isActive) return;

        const upcomingCursos = getUpcomingItems(cursos, "curso");
        const upcomingActividades = getUpcomingItems(actividades, "actividad");

        setGlobalAcademias(
          [...upcomingCursos, ...upcomingActividades]
            .sort((a, b) => a.startDate - b.startDate),
        );
      })
      .catch((error) => {
        if (isActive && error.name !== "AbortError") {
          setGlobalAcademias([]);
        }
      });

    return () => {
      isActive = false;
      controller.abort();
    };
  }, []);

  const handleNavToggle = () => {
    setShowNav((current) => !current);
  };

  const firstName = normalizeTitle(maestra?.nombre?.split(" ")?.[0] || "Paula");
  const fullName = maestra?.nombre || "Paula Abarca";
  const profileImage = maestra?.imagen;
  const personalCursos = getUpcomingItems(maestra?.cursos_list, "curso");
  const personalActividades = getUpcomingItems(
    maestra?.actividades_list,
    "actividad",
  );
  const personalEvents = [...personalCursos, ...personalActividades].sort(
    (a, b) => a.startDate - b.startDate,
  );
  const subscribedEventKeys = new Set(
    personalEvents.map((event) => `${event.type}-${event.id}`),
  );
  const discoveryAcademias = globalAcademias
    .filter((academia) => {
      const isAlreadySubscribed =
        subscribedEventKeys.has(`${academia.type}-${academia.id}`) ||
        academia.elegidas?.some((elegida) => elegida.rut === PAULA_RUT);

      return !isAlreadySubscribed;
    })
    .slice(0, 2);
  const nextEvent = personalEvents[0];
  const todayEvents = personalEvents.filter(
    (event) => event.startDate?.getTime() === getToday().getTime(),
  );

  return (
    <main
      className={`mobile-home-prototype ${
        chromeHidden ? "mobile-home-chrome-hidden" : ""
      }`}
    >
      <section className="mobile-home-phone" aria-label="Prototipo mobile">
        <header className="mobile-home-header">
          <button
            type="button"
            className="mobile-home-menu"
            aria-label="Abrir menu"
            onClick={handleNavToggle}
          >
            <span />
            <span />
            <span />
          </button>
          <Link to="/" className="mobile-home-brand">
            RedMaestra
          </Link>
        </header>

        <section className="mobile-home-profile">
          <PersonAvatar src={profileImage} name={fullName} alt={`Fotografía de ${fullName}`} className="mobile-home-profile-avatar" tone="maestra" />
          <div>
            <p>¡Buenos días!</p>
            <h2>{normalizeTitle(fullName)}</h2>
          </div>
          <button
            type="button"
            className="mobile-home-bell"
            aria-label="Notificaciones"
          >
            <FaBell />
            <span />
          </button>
        </section>

        <section className="mobile-home-highlight mobile-home-today">
          <div>
            <span>Hoy</span>
            <strong>
              {isLoadingProfile
                ? "Cargando actividades..."
                : todayEvents.length > 0
                  ? `${firstName} tiene ${todayEvents.length} actividad${
                      todayEvents.length === 1 ? "" : "es"
                    } programada${todayEvents.length === 1 ? "" : "s"}.`
                  : "No tienes actividades programadas."}
            </strong>
          </div>
          <div className="mobile-home-calendar-graphic">
            <IoCalendarOutline />
            <span />
          </div>
        </section>

        {profileError ? (
          <section className="mobile-home-highlight mobile-home-event mobile-home-error">
            <div>
              <span>Perfil</span>
              <strong>{profileError}</strong>
            </div>
          </section>
        ) : (
          <section className="mobile-home-highlight mobile-home-event">
            <div>
              <span>Próximo evento</span>
              <strong>
                {normalizeTitle(nextEvent?.nombre) || "Sin eventos próximos"}
              </strong>
              <p>
                <FaCalendarAlt /> {formatEventDate(nextEvent?.startDate)}
              </p>
              <p>
                <FaMapMarkerAlt /> {nextEvent?.comuna || "Ubicación por confirmar"}
              </p>
            </div>
            <button type="button" aria-label="Ver proximo evento">
              <FaChevronRight />
            </button>
          </section>
        )}

        <section className="mobile-home-section">
          <div className="mobile-home-section-title">
            <h3>Próximas Academias</h3>
            <button type="button">Ver todo</button>
          </div>
          {discoveryAcademias.length > 0 ? (
            discoveryAcademias.map((academia) => (
              <article
                className="mobile-home-academy-card"
                key={`${academia.type}-${academia.id}`}
              >
                <div>
                  <h4>{normalizeTitle(academia.nombre)}</h4>
                  <p>
                    <FaCalendarAlt /> {formatDate(academia.startDate)}
                  </p>
                  <p>
                    <FaMapMarkerAlt /> {academia.comuna || "Por confirmar"}
                  </p>
                </div>
                <button type="button">Ver detalles</button>
              </article>
            ))
          ) : (
            <p className="mobile-home-empty-state">
              No hay academias próximas disponibles.
            </p>
          )}
        </section>

        <section className="mobile-home-section">
          <div className="mobile-home-section-title">
            <h3>Trabajos disponibles</h3>
            <button type="button">Ver todo</button>
          </div>
          <div className="mobile-home-jobs">
            <article className="mobile-home-job-card">
              <div className="mobile-home-job-heading">
                <h4>{normalizeTitle("Electricidad San Joaquín")}</h4>
                <span>
                  <FaBoltLightning />
                </span>
              </div>
              <p>Hace 15 min</p>
              <button type="button">Ver detalles</button>
            </article>

            <article className="mobile-home-job-card">
              <div className="mobile-home-job-heading">
                <h4>{normalizeTitle("Gasfitería Ñuñoa")}</h4>
                <span>
                  <FaTint />
                </span>
              </div>
              <p>Hace 3 hrs</p>
              <button type="button">Ver detalles</button>
            </article>
          </div>
        </section>

        <nav className="mobile-home-bottom-nav" aria-label="Navegacion mobile">
          <button type="button" aria-label="Inicio">
            <FaHome />
          </button>
          <button type="button" aria-label="Calendario">
            <FaCalendarAlt />
          </button>
          <button type="button" aria-label="Comunidad">
            <FaUsers />
          </button>
          <button type="button" aria-label="Trabajo">
            <FaBriefcase />
          </button>
          <button type="button" aria-label="Notificaciones">
            <span className="mobile-home-nav-alert">
              <FaBell />
            </span>
          </button>
          <button type="button" aria-label="Perfil">
            <FaUser />
          </button>
        </nav>
      </section>

      <Offcanvas show={showNav} onHide={handleNavToggle} placement="start">
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>Menu</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <Indice handleOffcanvasToggle={handleNavToggle} />
        </Offcanvas.Body>
      </Offcanvas>
    </main>
  );
};

export default MobileHomePrototypePage;
